import { useState } from "react";

import { Box, Dialog, DialogContent, Tooltip, Zoom } from "@mui/material";
import { useConfirm } from "material-ui-confirm";

import InfoText from "../../../common/components/box/InfoText";
import ReplaceNodeIconButton from "../../../common/components/icons/ReplaceNodeIconButton";
import confirmDialogStyleOptions from "../../../common/helpers/confirmDialogParams";
import { nodeScriptVariable } from "../variables/createNode.variable";
import RunNodeScriptStep from "../nodes-create/components/RunNodeScriptStep";
import { useReplaceNode } from "../hooks/useReplaceNode";

interface IReplaceNodeContainerProps {
  nodeId: string;
  name: string;
  isOnline: boolean;
}

const ReplaceNodeContainer = ({
  nodeId,
  name,
  isOnline,
}: IReplaceNodeContainerProps): JSX.Element => {
  const confirm = useConfirm();
  const { replaceNode, loading } = useReplaceNode();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleClick = (): void => {
    confirm({
      title: `Replace certificate for ${name}?`,
      confirmationText: "Replace",
      cancellationText: "Cancel",
      content:
        "This will revoke the existing IoT certificate and create a new one. The node will go offline until the new certificate is installed.",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        color: "primary",
        disabled: loading,
      },
    })
      .then(async (): Promise<void> => {
        const result = await replaceNode(nodeId, name);

        const onboardCommand = result?.data?.replaceNode?.onboardCommand;

        if (onboardCommand) {
          nodeScriptVariable(onboardCommand);

          setDialogOpen(true);
        }
      })
      .catch(() => {});
  };

  const closeDialog = (): void => {
    setDialogOpen(false);
  };

  return (
    <>
      <Tooltip
        TransitionComponent={Zoom}
        arrow
        disableInteractive
        placement="left"
        title={
          isOnline ? "Cannot replace a node that is online" : "Replace Node"
        }
      >
        <Box
          component="span"
          sx={{
            height: "32px",
            opacity: isOnline ? 0.5 : 1,
            cursor: isOnline ? "default" : "pointer",
          }}
        >
          <ReplaceNodeIconButton onClick={isOnline ? undefined : handleClick} />
        </Box>
      </Tooltip>

      <Dialog
        open={dialogOpen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "2em",
          },
        }}
      >
        <DialogContent>
          <RunNodeScriptStep onBoardScript closeDialog={closeDialog} />

          <InfoText>
            After the node has successfully re-onboarded, use the{" "}
            <strong>Resend Config</strong> button (cloud sync icon) in the nodes
            table to push the node configuration.
          </InfoText>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReplaceNodeContainer;
